import { useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

// ============================================
// Global Ctrl+Enter Form Submission Handler
// ============================================
//
// Instead of each FormActionButtons instance registering its own
// document-level keydown listener (which causes ordering issues and
// cross-form interference), we use a single global listener with a
// registry pattern.
//
// How it works:
// 1. ONE document-level keydown listener is registered (once)
// 2. Each FormActionButtons registers its handler in a Map, keyed by formId
// 3. On Ctrl+Enter, we find the closest <form> ancestor of the event target
// 4. Look up the handler for that form's ID in the registry
// 5. Only THAT handler fires — no cross-form interference
//
// This correctly handles:
// - Dialog forms (Radix portals render outside parent forms in the DOM):
//   target.closest('form') returns the DIALOG's form, not the parent form
// - Autocomplete Ctrl+Enter (to open create dialogs): React 18 delegates
//   events to the root element, which fires BEFORE our document listener.
//   The autocomplete's onKeyDown calls e.preventDefault(), and our handler
//   checks e.defaultPrevented → skip.
// - Safety: if ANY dialog is open and the matched form is NOT inside
//   a dialog, we skip to prevent submitting underlying forms.
// ============================================

interface CtrlEnterHandler {
	canHandle: () => boolean;
	submit: () => void;
}

const formHandlerRegistry = new Map<string, CtrlEnterHandler>();
let globalListenerRef: ((e: KeyboardEvent) => void) | null = null;
let isGlobalListenerRegistered = false;

function ensureGlobalCtrlEnterListener() {
	if (isGlobalListenerRegistered) return;
	isGlobalListenerRegistered = true;

	const handler = (e: KeyboardEvent) => {
		// Only handle Ctrl+Enter
		if (e.key !== 'Enter' || !e.ctrlKey) return;

		// Ignore auto-repeated key events
		if (e.repeat) return;

		// Skip if already handled (e.g., autocomplete's React onKeyDown
		// called e.preventDefault() to open a create dialog).
		// This works because React 18 delegates events to the root element,
		// which fires BEFORE this document-level listener during bubbling.
		if (e.defaultPrevented) return;

		const target = e.target as HTMLElement;
		const closestForm = target.closest('form');

		if (closestForm?.id && formHandlerRegistry.has(closestForm.id)) {
			// Safety: if a dialog is open and this form is NOT inside a dialog,
			// skip to prevent submitting underlying forms when a modal has focus.
			const formIsInDialog = closestForm.closest('[role="dialog"]') !== null;
			if (!formIsInDialog) {
				const openDialogs = document.querySelectorAll(
					'[role="dialog"][data-state="open"]'
				);
				if (openDialogs.length > 0) return;
			}

			const formHandler = formHandlerRegistry.get(closestForm.id)!;
			if (formHandler.canHandle()) {
				e.preventDefault();
				formHandler.submit();
			}
		}
	};

	globalListenerRef = handler;
	document.addEventListener('keydown', handler);
}

// Clean up on HMR to prevent stale listeners accumulating in development
if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		if (globalListenerRef) {
			document.removeEventListener('keydown', globalListenerRef);
			globalListenerRef = null;
		}
		isGlobalListenerRegistered = false;
		formHandlerRegistry.clear();
	});
}

interface FormActionButtonsProps {
	onSubmit?: () => void;
	isSubmitting?: boolean;
	/**
	 * Optional custom submit label. If not provided, uses translation based on mode.
	 * @deprecated Prefer using mode prop instead
	 */
	submitLabel?: string;
	/**
	 * Form mode to determine the submit button label
	 * 'create' shows "Create", 'edit' shows "Save changes", 'clone' shows "Create" (same as create)
	 */
	mode?: 'create' | 'edit' | 'clone';
	submitType?: 'submit' | 'button';
	submitVariant?:
		| 'default'
		| 'destructive'
		| 'outline'
		| 'secondary'
		| 'ghost'
		| 'link';
	disabled?: boolean;
	className?: string;
	/**
	 * Validation errors from react-hook-form formState.errors
	 * Used to show inline validation error message
	 */
	validationErrors?: Record<string, any>;
	/**
	 * Callback to scroll to first error field
	 * If not provided, default behavior will attempt to focus first invalid field
	 */
	onScrollToError?: () => void;
	/**
	 * Form ID to associate the submit button with a form element.
	 * Required when the button is rendered outside the form (e.g., in header).
	 * The form element should have a matching id attribute.
	 */
	formId?: string;
}

export const FormActionButtons = ({
	onSubmit,
	isSubmitting = false,
	submitLabel,
	mode = 'edit',
	submitType = 'submit',
	submitVariant = 'default',
	disabled = false,
	className = '',
	validationErrors = {},
	onScrollToError,
	formId
}: FormActionButtonsProps) => {
	const { t } = useTranslation();
	const isDisabled = disabled || isSubmitting;
	const submitButtonRef = useRef<HTMLButtonElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// Ref keeps current callback/state values accessible to the global handler
	// without re-registering on every render or prop change.
	const handlerStateRef = useRef({
		isDisabled,
		submitType,
		onSubmit,
		formId,
		submitButtonRef
	});
	handlerStateRef.current = {
		isDisabled,
		submitType,
		onSubmit,
		formId,
		submitButtonRef
	};

	// Register/unregister with the global Ctrl+Enter handler registry.
	// Only depends on formId — reactive state is accessed via ref.
	useEffect(() => {
		ensureGlobalCtrlEnterListener();

		if (formId) {
			formHandlerRegistry.set(formId, {
				canHandle: () => !handlerStateRef.current.isDisabled,
				submit: () => {
					const state = handlerStateRef.current;
					if (state.submitType === 'submit' && state.formId) {
						const form = document.getElementById(
							state.formId
						) as HTMLFormElement;
						if (form) form.requestSubmit();
					} else if (state.onSubmit) {
						state.onSubmit();
					} else if (state.submitButtonRef.current) {
						state.submitButtonRef.current.click();
					}
				}
			});

			return () => {
				formHandlerRegistry.delete(formId);
			};
		} else {
			// Fallback for FormActionButtons without formId (shouldn't happen
			// in current codebase but kept as safety net). Uses dialog detection.
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key !== 'Enter' || !e.ctrlKey) return;
				if (e.repeat) return;
				if (e.defaultPrevented) return;
				if (handlerStateRef.current.isDisabled) return;

				const ourDialog = containerRef.current?.closest('[role="dialog"]');
				if (!ourDialog) {
					const openDialogs = document.querySelectorAll(
						'[role="dialog"][data-state="open"]'
					);
					if (openDialogs.length > 0) return;
				}

				e.preventDefault();
				const state = handlerStateRef.current;
				if (state.onSubmit) {
					state.onSubmit();
				} else if (state.submitButtonRef.current) {
					state.submitButtonRef.current.click();
				}
			};

			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}
	}, [formId]);

	// Use translations with fallback to custom labels
	const getDefaultLabel = () => {
		switch (mode) {
			case 'create':
				return t('buttons.create');
			case 'clone':
				return t('buttons.clone');
			default:
				return t('form.saveChanges');
		}
	};
	const finalSubmitLabel = submitLabel ?? getDefaultLabel();

	// Count total validation errors (including nested fields)
	const countErrors = (errors: Record<string, any>): number => {
		let count = 0;
		for (const key in errors) {
			const error = errors[key];
			if (error) {
				if (Array.isArray(error)) {
					// Handle array fields (e.g., vehicles in contracts)
					error.forEach(item => {
						if (item && typeof item === 'object') {
							count += countErrors(item);
						}
					});
				} else if (typeof error === 'object' && error.message) {
					// Direct error with message
					count++;
				} else if (typeof error === 'object') {
					// Nested object errors
					count += countErrors(error);
				}
			}
		}
		return count;
	};

	const errorCount = countErrors(validationErrors);
	const hasErrors = errorCount > 0;

	// Delay for smooth scroll animation before attempting focus
	const FOCUS_DELAY_MS = 300;

	const handleScrollToError = () => {
		if (onScrollToError) {
			onScrollToError();
		} else {
			// Default behavior: find and focus first invalid field
			const firstError = document.querySelector('[aria-invalid="true"]');
			if (firstError) {
				firstError.scrollIntoView({
					behavior: 'smooth',
					block: 'center'
				});
				// Try to focus the element if it's focusable
				if (firstError instanceof HTMLElement) {
					setTimeout(() => firstError.focus(), FOCUS_DELAY_MS);
				}
			}
		}
	};

	return (
		<div ref={containerRef} className={`flex items-center gap-4 ${className}`}>
			{/* Submit button with keyboard shortcut hint */}
			<div className="flex items-center">
				<Button
					ref={submitButtonRef}
					type={submitType}
					variant={submitVariant}
					onClick={onSubmit}
					disabled={isDisabled}
					form={formId}
					className="gap-2"
				>
					{isSubmitting ? t('form.saving') : finalSubmitLabel}
					{!isSubmitting && (
						<span className={`hidden sm:inline text-[10px] font-normal opacity-60 ${isDisabled ? 'opacity-30' : ''}`}>
							Ctrl+Enter
						</span>
					)}
				</Button>
			</div>
			{/* Validation errors */}
			{hasErrors && !isSubmitting && (
				<Alert
					variant="destructive"
					className="transition-colors cursor-pointer hover:bg-destructive/5 py-2"
					onClick={handleScrollToError}
				>
					<AlertCircle className="w-4 h-4" />
					<AlertDescription>
						{errorCount === 1
							? t('form.validationErrorSingular')
							: t('form.validationErrorPlural', { count: errorCount })}
					</AlertDescription>
				</Alert>
			)}
		</div>
	);
};
